<template>
  <div class="w-full flex flex-col gap-y-5">
    <div v-if="capabilities.connectionFields" class="max-w-900">
      <div class="text-13 font-700 mb-2">Connection</div>
      <div class="grid gap-3 lg:grid-cols-2">
        <template v-if="isSqlAdapter">
          <FormInput v-model="form.connection.host" label="Host" :error="errors.host" placeholder="db.example.com" @update:modelValue="emitChange" />
          <FormInput v-model="form.connection.port" label="Port" type="number" :error="errors.port" @update:modelValue="emitChange" />
          <FormInput v-model="form.connection.database" label="Database" :error="errors.database" @update:modelValue="emitChange" />
          <FormInput v-if="isPostgresAdapter" v-model="form.connection.schema" label="Schema" placeholder="public" @update:modelValue="emitChange" />
          <FormInput v-model="form.connection.tunnel_id" label="Tunnel ID" placeholder="Optional" @update:modelValue="emitChange" />
          <div class="flex items-center gap-x-2 mt-5">
            <Toggle v-model="form.connection.ssl" dense @update:modelValue="emitChange" />
            <span class="text-13 font-500">SSL</span>
          </div>
          <div class="flex flex-col gap-y-1 lg:col-span-2">
            <div class="flex items-center gap-x-2">
              <Toggle v-model="useStaticIpProxy" dense :disabled="!staticProxyIps.length && !useStaticIpProxy" />
              <span class="text-13 font-500">Use Static IP Proxy Service</span>
            </div>
            <div v-if="useStaticIpProxy && staticProxyIps.length" class="text-12 text-semi-dark-text">
              Connections come from {{ staticProxyIps.join(', ') }}. Allowlist {{ staticProxyIps.length > 1 ? 'these IPs' : 'this IP' }} on your database.
            </div>
            <div v-else-if="!staticProxyIps.length" class="text-12 text-semi-dark-text">The Static IP Proxy Service is not available in this environment.</div>
            <div v-else class="text-12 text-semi-dark-text">For databases that only accept allowlisted IPs.</div>
          </div>
        </template>

        <template v-else-if="isD1Adapter">
          <FormInput v-model="d1AccountId.value" label="Account ID" :error="errors.account_id" @update:modelValue="emitChange" />
          <FormInput v-model="d1ApiToken.value" label="API token" type="password" :error="errors.api_token" @focus="onSecretFocus(d1ApiToken)" @update:modelValue="emitChange" />
          <div class="flex items-end gap-x-2 lg:col-span-2">
            <PillButton v-if="d1CredentialsReady" text="Load databases" :loading="loadingD1Databases" :disabled="!d1CredentialsReady" @click="loadD1Databases" />
          </div>
          <FormSelect
            v-if="d1DatabasesLoaded"
            v-model="form.connection.database_id"
            label="Database"
            class="w-full lg:w-1/2"
            style="min-width: 50%"
            :options="d1DatabaseOptions"
            default-reducer
            :error="errors.database_id"
            @update:modelValue="onD1DatabaseChange"
          />
        </template>

        <template v-else-if="isDatabricksAdapter">
          <FormSelect v-model="form.connection.auth_type" label="Connection type" :options="databricksAuthOptions" default-reducer @update:modelValue="onDatabricksAuthTypeChange" />
          <FormInput v-model="form.connection.host" label="Server hostname" :error="errors.host" placeholder="adb-123.azuredatabricks.net" @update:modelValue="emitChange" />
          <FormInput v-model="form.connection.http_path" label="HTTP path" :error="errors.http_path" placeholder="/sql/1.0/warehouses/..." @update:modelValue="emitChange" />
        </template>

        <template v-else-if="isAzureBlobAdapter">
          <FormInput v-model="form.connection.account" label="Storage account" :error="errors.account" placeholder="mystorageaccount" @update:modelValue="emitChange" />
          <FormInput v-model="form.connection.container" label="Container" :error="errors.container" placeholder="bronze" @update:modelValue="emitChange" />
          <div class="text-12 text-semi-dark-text lg:col-span-2">The SAS token needs Create and Write permissions (sp=cw) on the container.</div>
        </template>
      </div>
    </div>

    <div v-if="showAuthentication" class="max-w-900">
      <div class="text-13 font-700 mb-2">Authentication</div>
      <div class="grid gap-3 lg:grid-cols-2">
        <FormInput
          v-for="param in authParams"
          :key="param.key"
          v-model="param.value"
          :label="param.label"
          :type="param.type === 'password' || param.type === 'token' ? 'password' : 'text'"
          :error="errors[param.key]"
          @focus="onSecretFocus(param)"
          @update:modelValue="emitChange"
        />
      </div>
    </div>

    <div v-if="editing" class="max-w-900 border border-solid border-stroke rounded-8 p-4">
      <div class="flex items-center justify-between gap-x-4">
        <div>
          <div class="text-13 font-700">Verification</div>
          <div class="text-12 text-semi-dark-text mt-1">{{ verificationText }}</div>
        </div>
        <PillButton text="Verify" primary :loading="verifying" @click="$emit('verify')" />
      </div>
    </div>

    <div v-if="capabilities.runtimeSettings" class="max-w-900">
      <div class="text-13 font-700 mb-2">Runtime Settings</div>
      <div class="grid gap-3 lg:grid-cols-3">
        <FormInput v-if="capabilities.concurrentCalls" v-model="form.settings.max_concurrent_calls" label="Concurrent calls" type="number" @update:modelValue="emitChange" />
        <FormInput v-model="form.settings.default_timeout_ms" label="Timeout (ms)" type="number" @update:modelValue="emitChange" />
        <FormInput v-model="form.settings.result_truncation.max_rows" label="Max rows (0 for all)" type="number" min="0" @update:modelValue="emitChange" />
      </div>
    </div>
  </div>
</template>

<script>
import { DATA_ADAPTERS } from '@/constants';
import { apiPostProjectDataAdapterDatabases } from '@/helpers/api';

// Connection categories mirror the backend connection flavors. The config form
// branches on category first (positive checks), and only by adapter key where a
// single adapter within a category genuinely differs.
const CATEGORY = { SQL: 'sql', D1: 'd1', DATABRICKS: 'databricks', REST_API: 'rest_api', AZURE_BLOB: 'azure_blob' };

const ADAPTER_CATEGORY = {
  [DATA_ADAPTERS.POSTGRESQL.key]: CATEGORY.SQL,
  [DATA_ADAPTERS.MYSQL.key]: CATEGORY.SQL,
  [DATA_ADAPTERS.MSSQL.key]: CATEGORY.SQL,
  [DATA_ADAPTERS.CLOUDFLARE_D1.key]: CATEGORY.D1,
  [DATA_ADAPTERS.DATABRICKS.key]: CATEGORY.DATABRICKS,
  [DATA_ADAPTERS.LUSHA.key]: CATEGORY.REST_API,
  [DATA_ADAPTERS.AZURE_BLOB.key]: CATEGORY.AZURE_BLOB,
};

// What each connection category exposes in the form.
//   connectionFields — show the Connection section
//   genericAuth      — show the generic Authentication section (d1 renders its creds inline)
//   runtimeSettings  — show the Runtime Settings section
//   concurrentCalls  — show the concurrent-calls setting
const CATEGORY_CAPABILITIES = {
  [CATEGORY.SQL]: { connectionFields: true, genericAuth: true, runtimeSettings: true, concurrentCalls: true },
  [CATEGORY.D1]: { connectionFields: true, genericAuth: false, runtimeSettings: true, concurrentCalls: false },
  [CATEGORY.DATABRICKS]: { connectionFields: true, genericAuth: true, runtimeSettings: true, concurrentCalls: true },
  [CATEGORY.REST_API]: { connectionFields: false, genericAuth: true, runtimeSettings: false, concurrentCalls: false },
  [CATEGORY.AZURE_BLOB]: { connectionFields: true, genericAuth: true, runtimeSettings: false, concurrentCalls: false },
};

function categoryOf(type) {
  return ADAPTER_CATEGORY[type] || CATEGORY.SQL;
}

const MASKED_VALUE = '********';

function defaultSettings() {
  return {
    max_concurrent_calls: 5,
    default_timeout_ms: 30000,
    result_truncation: { max_rows: 500, max_response_bytes: 524288, truncation_strategy: 'rows' },
  };
}

function defaultConnection(type) {
  if (type === DATA_ADAPTERS.POSTGRESQL.key) return { host: '', port: 5432, database: '', schema: 'public', ssl: true, tunnel_id: '', egress: { mode: 'direct' } };
  if (type === DATA_ADAPTERS.MYSQL.key) return { host: '', port: 3306, database: '', ssl: true, tunnel_id: '', egress: { mode: 'direct' } };
  if (type === DATA_ADAPTERS.MSSQL.key) return { host: '', port: 1433, database: '', ssl: true, tunnel_id: '', egress: { mode: 'direct' } };
  if (type === DATA_ADAPTERS.CLOUDFLARE_D1.key) return { account_id: '', database_id: '', database_name: '' };
  if (type === DATA_ADAPTERS.DATABRICKS.key) return { host: '', http_path: '', auth_type: 'personal' };
  if (type === DATA_ADAPTERS.LUSHA.key) return { base_url: 'https://api.lusha.com' };
  if (type === DATA_ADAPTERS.AZURE_BLOB.key) return { account: '', container: '' };
  return {};
}

function defaultAuthParams(type) {
  if (categoryOf(type) === CATEGORY.SQL) {
    return [
      { key: 'username', label: 'Username', type: 'text', value: '', required: true },
      { key: 'password', label: 'Password', type: 'password', value: '', required: true },
    ];
  }
  if (type === DATA_ADAPTERS.CLOUDFLARE_D1.key)
    return [
      { key: 'api_token', label: 'API token', type: 'token', value: '', required: true },
      { key: 'account_id', label: 'Account ID', type: 'text', value: '', required: true },
    ];
  if (type === DATA_ADAPTERS.DATABRICKS.key) return [{ key: 'token', label: 'Personal access token', type: 'token', value: '', required: true }];
  if (type === DATA_ADAPTERS.LUSHA.key) return [{ key: 'api_key', label: 'API key', type: 'token', value: '', required: true, placement: 'header', placement_key: 'api_key' }];
  if (type === DATA_ADAPTERS.AZURE_BLOB.key) return [{ key: 'sas_token', label: 'SAS token', type: 'token', value: '', required: true }];
  return [];
}

function databricksAuthParams(authType) {
  if (authType === 'm2m') {
    return [
      { key: 'client_id', label: 'Client ID', type: 'text', value: '', required: true },
      { key: 'client_secret', label: 'Client secret', type: 'password', value: '', required: true },
    ];
  }
  return [{ key: 'token', label: 'Personal access token', type: 'token', value: '', required: true }];
}
export default {
  name: 'DataAdaptersConfiguration',
  props: {
    adapter: Object,
    type: String,
    verifying: Boolean,
  },
  emits: ['update', 'verify'],
  data() {
    return {
      form: {
        type: this.type || DATA_ADAPTERS.POSTGRESQL.key,
        name: '',
        connection: {},
        auth_params: [],
        settings: defaultSettings(),
      },
      errors: {},
      loadingD1Databases: false,
      d1Databases: [],
      d1DatabasesLoaded: false,
    };
  },
  computed: {
    projectId() {
      return this.$route.params.projectId;
    },
    adapterType() {
      return this.adapter?.adapter_type || this.$route.params.dataAdapter || this.form.type;
    },
    editing() {
      return Boolean(this.adapter);
    },
    adapterCategory() {
      return categoryOf(this.form.type);
    },
    capabilities() {
      return CATEGORY_CAPABILITIES[this.adapterCategory] || CATEGORY_CAPABILITIES[CATEGORY.SQL];
    },
    /**
     * @returns {boolean} Whether this is a PostgreSQL adapter, the only SQL adapter with a schema setting.
     */
    isPostgresAdapter() {
      return this.form.type === DATA_ADAPTERS.POSTGRESQL.key;
    },
    isSqlAdapter() {
      return this.adapterCategory === CATEGORY.SQL;
    },
    /**
     * @returns {string[]} Static IPs the proxy connects from, for users to allowlist. Empty when the service is unavailable.
     */
    staticProxyIps() {
      return this.$store.state.staticProxyIps || [];
    },
    useStaticIpProxy: {
      /**
       * @returns {boolean} Whether this adapter connects through the Static IP Proxy Service.
       */
      get() {
        return this.form.connection.egress?.mode === 'static_ip';
      },
      /**
       * @param {boolean} value True to connect through the Static IP Proxy Service, false to connect directly.
       * @returns {void}
       */
      set(value) {
        this.form.connection.egress = { mode: value ? 'static_ip' : 'direct' };
        this.emitChange();
      },
    },
    isD1Adapter() {
      return this.adapterCategory === CATEGORY.D1;
    },
    isDatabricksAdapter() {
      return this.adapterCategory === CATEGORY.DATABRICKS;
    },
    /**
     * @returns {boolean} Whether this is an Azure Blob Storage adapter (account, container and SAS token).
     */
    isAzureBlobAdapter() {
      return this.adapterCategory === CATEGORY.AZURE_BLOB;
    },
    showAuthentication() {
      return this.authParams.length > 0 && this.capabilities.genericAuth;
    },
    authParams() {
      return this.form.auth_params;
    },
    d1ApiToken() {
      return this.form.auth_params.find((param) => param.key === 'api_token') || {};
    },
    d1AccountId() {
      return this.form.auth_params.find((param) => param.key === 'account_id') || {};
    },
    d1CredentialsReady() {
      return Boolean(this.d1AccountId.value && this.d1ApiToken.value);
    },
    d1DatabaseOptions() {
      return this.d1Databases.map((database) => ({ label: database.database_name, value: database.database_id }));
    },
    databricksAuthOptions() {
      return [
        { label: 'Personal access token', value: 'personal' },
        { label: 'OAuth machine-to-machine', value: 'm2m' },
      ];
    },
    verificationText() {
      const connection = this.form.connection || {};
      if (connection.verified) return connection.verified_at ? `Connected at ${connection.verified_at}` : 'Connected';
      return this.formatVerificationError(connection.verification_error) || 'Not verified';
    },
  },
  created() {
    this.loadAdapter();
  },
  methods: {
    formatVerificationError(error) {
      if (!error || typeof error !== 'string') return error;
      try {
        const data = JSON.parse(error);
        return data.errors?.map((item) => item.message).join('; ') || data.message || error;
      } catch (e) {
        return error;
      }
    },
    loadAdapter() {
      const payload = this.adapter?.payload || {};
      const type = this.adapter?.type || this.type || DATA_ADAPTERS.POSTGRESQL.key;
      const connection = { ...defaultConnection(type), ...(payload.connection || {}) };
      const authParams = payload.connection?.auth?.params?.length ? payload.connection.auth.params.map((param) => ({ ...param })) : defaultAuthParams(type);
      this.form = {
        type,
        name: payload.name || '',
        connection,
        auth_params: authParams,
        settings: { ...defaultSettings(), ...(payload.settings || {}), result_truncation: { ...defaultSettings().result_truncation, ...(payload.settings?.result_truncation || {}) } },
      };
      if (categoryOf(type) === CATEGORY.D1 && connection.database_id && connection.database_name) {
        this.d1Databases = [{ database_id: connection.database_id, database_name: connection.database_name }];
      }
      this.d1DatabasesLoaded = false;
      this.emitChange();
      if (this.editing && categoryOf(type) === CATEGORY.D1) this.loadD1Databases();
    },
    onTypeChange() {
      this.form.connection = defaultConnection(this.form.type);
      this.form.auth_params = defaultAuthParams(this.form.type);
      this.emitChange();
    },
    onDatabricksAuthTypeChange() {
      this.form.auth_params = databricksAuthParams(this.form.connection.auth_type);
      this.emitChange();
    },
    async loadD1Databases() {
      if (!this.d1CredentialsReady && !this.editing) return;
      this.loadingD1Databases = true;
      const response = await apiPostProjectDataAdapterDatabases({
        project_id: this.projectId,
        adapter_type: this.adapterType,
        connection: this.form.connection,
        auth_params: this.form.auth_params,
      });
      this.loadingD1Databases = false;
      if (response.status === 200) {
        this.d1Databases = response.data.databases || [];
        this.d1DatabasesLoaded = true;
        this.onD1DatabaseChange();
      }
    },
    onD1DatabaseChange() {
      const selected = this.d1Databases.find((database) => database.database_id === this.form.connection.database_id);
      this.form.connection.database_name = selected?.database_name || '';
      this.emitChange();
    },
    onSecretFocus(param) {
      if (param.masked || param.value === MASKED_VALUE) {
        param.value = '';
        param.masked = false;
        this.emitChange();
      }
    },
    validate() {
      const errors = {};
      if (this.isSqlAdapter) {
        if (!this.form.connection.host) errors.host = 'Host is required';
        if (!this.form.connection.port) errors.port = 'Port is required';
        if (!this.form.connection.database) errors.database = 'Database is required';
      }
      if (this.isD1Adapter && !this.form.connection.database_id) errors.database_id = 'Database is required';
      if (this.isDatabricksAdapter) {
        if (!this.form.connection.host) errors.host = 'Host is required';
        if (!this.form.connection.http_path) errors.http_path = 'HTTP path is required';
      }
      if (this.isAzureBlobAdapter) {
        if (!this.form.connection.account) errors.account = 'Storage account is required';
        if (!this.form.connection.container) errors.container = 'Container is required';
      }
      this.form.auth_params.forEach((param) => {
        if (param.required && !param.value) errors[param.key] = `${param.label} is required`;
      });
      this.errors = errors;
      return { valid: Object.keys(errors).length === 0, errors };
    },
    getPayload() {
      const connection = { ...this.form.connection };
      delete connection.auth;
      const payload = {
        type: this.form.type,
        connection,
        auth_params: this.form.auth_params.map((param) => ({ ...param })),
      };
      // Only adapters that expose runtime settings push them on save; API adapters
      // (no settings UI) let the backend keep its minimal defaults.
      if (this.capabilities.runtimeSettings) payload.settings = this.form.settings;
      return payload;
    },
    emitChange() {
      this.$emit('update', this.getPayload());
    },
  },
  watch: {
    adapter() {
      this.loadAdapter();
    },
  },
};
</script>
